import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';

export default function Analytics({ onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    const session = localStorage.getItem('session');
    if (!session) return;
    try {
      const part = session.split('.')[1];
      const padded = part + '='.repeat((4 - part.length % 4) % 4);
      const payload = JSON.parse(atob(padded.replace(/-/g,'+').replace(/_/g,'/')));
      fetch(`${API_BASE}/state/${payload.guild_id}`, { headers: { 'Authorization': 'Bearer ' + session } })
        .then(r => r.json())
        .then(d => setData(d.analytics || {}));
    } catch {}
  }, []);

  if (!data) return null;

  const topSpells = Object.entries(data.top_spells || {}).sort((a,b) => b[1]-a[1]).slice(0, 8);
  const topEnemies = Object.entries(data.top_enemies || {}).sort((a,b) => b[1]-a[1]).slice(0, 8);
  const maxSpell = topSpells[0]?.[1] || 1;
  const maxEnemy = topEnemies[0]?.[1] || 1;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card modal-wide" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📈 Campaign Analytics</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body analytics-body">
          {/* Stat Cards */}
          <div className="analytics-cards">
            <div className="stat-card"><div className="stat-value">{data.combats_total || 0}</div><div className="stat-label">Combats</div></div>
            <div className="stat-card"><div className="stat-value">{data.enemies_killed || 0}</div><div className="stat-label">Enemies Slain</div></div>
            <div className="stat-card"><div className="stat-value">{data.spells_cast || 0}</div><div className="stat-label">Spells Cast</div></div>
            <div className="stat-card"><div className="stat-value">{(data.total_damage_dealt || 0).toLocaleString()}</div><div className="stat-label">Damage Dealt</div></div>
            <div className="stat-card"><div className="stat-value">{(data.total_damage_taken || 0).toLocaleString()}</div><div className="stat-label">Damage Taken</div></div>
            <div className="stat-card"><div className="stat-value">{data.deaths || 0}</div><div className="stat-label">Deaths</div></div>
            <div className="stat-card"><div className="stat-value">{(data.total_xp_awarded || 0).toLocaleString()}</div><div className="stat-label">XP Awarded</div></div>
            <div className="stat-card"><div className="stat-value">{data.loot_drops || 0}</div><div className="stat-label">Loot Drops</div></div>
            <div className="stat-card"><div className="stat-value">{data.natural_20_count || 0}</div><div className="stat-label">Natural 20s</div></div>
            <div className="stat-card"><div className="stat-value">{data.max_single_hit || 0}</div><div className="stat-label">Max Single Hit</div></div>
            <div className="stat-card"><div className="stat-value">{data.heals_given || 0}</div><div className="stat-label">Heals Given</div></div>
            <div className="stat-card"><div className="stat-value">{data.conditions_inflicted || 0}</div><div className="stat-label">Debuffs Inflicted</div></div>
            <div className="stat-card"><div className="stat-value">{(data.total_gold_earned || 0).toLocaleString()}</div><div className="stat-label">Gold Earned</div></div>
            <div className="stat-card"><div className="stat-value">{data.combats_no_deaths || 0}</div><div className="stat-label">Flawless Runs</div></div>
            <div className="stat-card"><div className="stat-value">{data.tpk_count || 0}</div><div className="stat-label">TPKs</div></div>
            <div className="stat-card"><div className="stat-value">{data.overkill_count || 0}</div><div className="stat-label">Overkills</div></div>
            <div className="stat-card"><div className="stat-value">{data.first_round_kills || 0}</div><div className="stat-label">First Round Kills</div></div>
            <div className="stat-card"><div className="stat-value">{data.flawless_combats || 0}</div><div className="stat-label">No Damage Taken</div></div>
            <div className="stat-card"><div className="stat-value">{data.last_stand_count || 0}</div><div className="stat-label">Last Stands</div></div>
            <div className="stat-card"><div className="stat-value">{data.max_combat_rounds || 0}</div><div className="stat-label">Longest Combat</div></div>
            <div className="stat-card"><div className="stat-value">{data.short_rests_taken || 0}</div><div className="stat-label">Short Rests</div></div>
            <div className="stat-card"><div className="stat-value">{data.long_rests_taken || 0}</div><div className="stat-label">Long Rests</div></div>
            <div className="stat-card"><div className="stat-value">{data.unique_combat_days || 0}</div><div className="stat-label">Sessions</div></div>
            <div className="stat-card"><div className="stat-value">{data.godmode_used || 0}</div><div className="stat-label">Godmodes</div></div>
          </div>

          {/* Top Spells */}
          <div className="analytics-section">
            <h3>✨ Most Cast Spells</h3>
            <div className="bar-chart">
              {topSpells.map(([name, count]) => (
                <div key={name} className="bar-row">
                  <span className="bar-label">{name}</span>
                  <div className="bar-track"><div className="bar-fill spell-bar" style={{ width: `${(count/maxSpell)*100}%` }}></div></div>
                  <span className="bar-count">{count}</span>
                </div>
              ))}
              {topSpells.length === 0 && <p className="analytics-empty">No spells cast yet.</p>}
            </div>
          </div>

          {/* Top Enemies */}
          <div className="analytics-section">
            <h3>💀 Most Killed Enemies</h3>
            <div className="bar-chart">
              {topEnemies.map(([name, count]) => (
                <div key={name} className="bar-row">
                  <span className="bar-label">{name}</span>
                  <div className="bar-track"><div className="bar-fill enemy-bar" style={{ width: `${(count/maxEnemy)*100}%` }}></div></div>
                  <span className="bar-count">{count}</span>
                </div>
              ))}
              {topEnemies.length === 0 && <p className="analytics-empty">No enemies killed yet.</p>}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
